<template>
  <div class="qc-iteration-dialog">
    <div class="dialog-overlay" @click="closeDialog">
      <div class="dialog-content" @click.stop>
        <!-- Header -->
        <div class="dialog-header">
          <div class="header-left">
            <div class="header-icon">
              <img
                src="~@/assets/images/icons/checklistGray.svg"
                alt="Checklist"
                class="checklist-icon"
              />
            </div>
            <div class="header-text">
              <h2 class="dialog-title">QC History</h2>
              <p class="dialog-subtitle">
                View and manage quality inspection records.
              </p>
            </div>
          </div>
          <button class="close-btn" @click="closeDialog">×</button>
        </div>

        <!-- Content -->
        <div class="dialog-body">
          <div class="content-container">
            <!-- Iterations List - Matching the exact style from sample.tsx -->
            <div class="iterations-list">
              <div
                v-for="(form, index) in formsData"
                :key="form.qcformId"
                class="iteration-card"
              >
                <div :class="['card-content',{'highlight-row': index === 0 && showFillInspection}]">
                  <!-- Top Row with Grid Layout -->
                  <div class="card-top-row">
                    <div class="grid-container">
                      <div class="grid-item">
                        <div class="label">Job Order No.</div>
                        <div class="value mono">{{ form?.jobOrderNo }}</div>
                      </div>
                      <div class="grid-item">
                        <div class="label">Part Name</div>
                        <div class="value">{{ form.partName }}</div>
                      </div>
                      <div class="grid-item">
                        <div class="label">Operation</div>
                        <div class="value">{{ form.operation }}</div>
                      </div>
                      <div class="grid-item">
                        <div class="label">Inspector</div>
                        <div class="value">{{ form.inspectorName }}</div>
                      </div>
                      <div class="grid-item">
                        <div class="label">Status</div>
                        <div
                          class="status-badge"
                          :class="getStatusClass(form.status?.label)"
                        >
                          <span class="status-icon">{{
                            getStatusIcon(form.status?.label)
                          }}</span>
                          {{ form.status?.label }}
                        </div>
                      </div>
                    </div>
                    <button
                      class="action-btn"
                      @click="
                        index === 0 && showFillInspection
                          ? updateQcform(form)
                          : viewInspection(form)
                      "
                    >
                      <svg
  v-if="index === 0 && showFillInspection"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M12 20h9"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <path
    d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>

                      <svg
                        v-else
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      {{ index === 0 && showFillInspection ? "Fill Inspection" : "View" }}
                    </button>
                  </div>

                  <!-- Bottom Row -->
                  <div class="card-bottom-row">
                    <div class="detail-item">
                      <span class="detail-label">Check Points:</span>
                      <span class="detail-value">{{ form.checkpoints }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Date:</span>
                      <span class="detail-value">{{
                        formatDate(form.updated_on)
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getformsDataByWFstepIdAndInstanceId } from '@/api'
import Loader from '@/plugins/loader'
import { alert } from '@/plugins/notification'
import config from '@/config'

export default {
  name: 'QCIterationListDialog',
  props: {
    stepHistory: {
      type: Array,
      required: true
    },
    stepId: {
      type: String,
      required: true
    },
    instanceId: {
      type: String,
      required: true
    },
    showFillInspection: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formsData: []
    }
  },
  methods: {
    async setup () {
      if (!this.instanceId || !this.stepId) {
        alert('Selected form is unavailable')
        this.$emit('close')
        return
      }
      const loader = new Loader()
      loader.show()
      try {
        const allFormRevisionData = await getformsDataByWFstepIdAndInstanceId(
          this.stepId,
          this.instanceId
        )
        this.formsData = []
        for (const form of allFormRevisionData.transition_history) {
          const obj = {
            qcformId: form.form_id,
            status: this.updateInspectionStatus(form.qc_form.status),
            operation: form.current_workflow_step.name,
            updated_on: form.qc_form.updated_on,
            qcTemplateId: form.qc_form.template_version.template_id
          }
          for (const item of form.qc_form.forms_metadata_by_id) {
            const value =
              item.time_value?.split('+')[0] ??
              item.int_value ??
              item.date_value ??
              item.bool_value ??
              item.point_value ??
              item.string_value ??
              '--'
            switch (item.field_id) {
            case config.DEFAULT_QC_FIELD_ID_MAPS.PART_NAME:
              obj.partName = value
              break
            case config.DEFAULT_QC_FIELD_ID_MAPS.PART_NUMBER:
              obj.partNumber = value
              break
              // case config.DEFAULT_QC_FIELD_ID_MAPS.operation:
              //   this.formData.operation = value
              //   break
            default:
              break
            }
          }
          for (const user of form.qc_form.forms_user_lists) {
            if (user.field_id === config.DEFAULT_QC_FIELD_ID_MAPS.INSPECTOR) {
              obj.inspectorName =
                user.core_user.first_name + ' ' + user.core_user.last_name
              break
            }
          }
          obj.checkpoints = 0
          for (const table of form.qc_form.forms_table) {
            obj.checkpoints += table?.table_data?.rows.length
          }
          this.formsData.push(obj)
        }
        loader.hide()
      } catch (err) {
        console.log(err)
        loader.hide()
      }
    },
    closeDialog () {
      this.$emit('close')
    },

    updateQcform (formData) {
      this.$emit('updateQcform', formData)
    },

    viewInspection (formData) {
      this.$emit('viewInspection', formData)
    },

    formatDate (dateString) {
      if (!dateString) return '--'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    getStatusClass (status) {
      const statusClasses = {
        'In Progress': 'in-progress',
        Approved: 'approved',
        Rejected: 'rejected',
        Hold: 'hold'
      }
      return statusClasses[status] || 'in-progress'
    },

    getStatusIcon (status) {
      const statusIcons = {
        'In Progress': '⏱',
        Approved: '✓',
        Rejected: '✗',
        Hold: '⏸'
      }
      return statusIcons[status] || '⏱'
    },
    updateInspectionStatus (statusId) {
      switch (statusId) {
      case config.FORM_STATE_MAP.IN_PROGRESS:
        return {
          value: config.FORM_STATE_MAP.IN_PROGRESS,
          label: 'In Progress'
        }
      case config.FORM_STATE_MAP.APPROVED:
        return { value: config.FORM_STATE_MAP.APPROVED, label: 'Approved' }
      case config.FORM_STATE_MAP.REJECTED:
        return { value: config.FORM_STATE_MAP.REJECTED, label: 'Rejected' }
      case config.FORM_STATE_MAP.HOLD:
        return { value: config.FORM_STATE_MAP.HOLD, label: 'Hold' }
      default:
        return {}
      }
    }
  },
  mounted () {
    this.setup()
  }
}
</script>

<style lang="scss" scoped>
.qc-iteration-dialog {
  .dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .dialog-content {
    background: white;
    border-radius: 8px;
    max-width: 1000px;
    width: 95%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }

  .dialog-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    background: white;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-radius: 8px 8px 0 0;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;

      .header-icon {
        width: 40px;
        height: 40px;
        background-color: #f97316;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;

        .checklist-icon {
          width: 20px;
          height: 20px;
          filter: brightness(0) invert(1);
        }
      }

      .header-text {
        .dialog-title {
          font-size: var(--text-2xl);
          font-weight: 500;
          color: #111827;
          margin: 0;
          line-height: 1.2;
          text-align: left;
        }

        .dialog-subtitle {
          font-size: var(--text-m);
          color: #6b7280;
          margin: 2px 0 0 0;
          line-height: 1.2;
          text-align: left;
        }
      }
    }

    .close-btn {
      background: none;
      border: none;
      font-size: var(--text-3xl);
      cursor: pointer;
      color: #6b7280;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;

      &:hover {
        background-color: #f3f4f6;
        color: #374151;
      }
    }
  }

  .dialog-body {
    flex: 1;
    overflow-y: auto;
    background-color: #f8f9fa;
    padding: 16px 20px;
    border-radius: 8px 8px 8px 8px;
    .content-container {
      max-width: 100%;
      margin: 0 auto;
    }
  }

  .iterations-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .iteration-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .card-content {
      padding: 12px 16px;
    }

    .card-top-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 8px;

      .grid-container {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
        gap: 16px;
        flex: 1;
        margin-right: 8px;
        min-width: 0; // Ensure consistent sizing
      }

      .grid-item {
        min-width: 0; // Prevent overflow
        overflow: hidden;

        .label {
          font-size: var(--text-xs2);
          color: #6b7280;
          margin-bottom: 1px;
          white-space: nowrap;
        }

        .value {
          font-size: var(--text-m);
          color: #111827;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          &.mono {
            font-family: monospace;
          }
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 6px;
          border-radius: 10px;
          font-size: var(--text-xs2);
          font-weight: 500;

          .status-icon {
            font-size: var(--text-xs1);
          }

          &.in-progress {
            background-color: #dbeafe;
            color: #1e40af;
          }

          &.approved {
            background-color: #dcfce7;
            color: #166534;
          }

          &.rejected {
            background-color: #fef2f2;
            color: #991b1b;
          }

          &.hold {
            background-color: #fefce8;
            color: #a16207;
          }
        }
      }

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border: 1px solid #d1d5db;
        border-radius: 5px;
        font-size: var(--text-m);
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        background: white;
        color: #374151;
        flex-shrink: 0;

        svg {
          width: 14px;
          height: 14px;
        }

        &:hover {
          background-color: #f9fafb;
          border-color: #9ca3af;
        }
      }
    }

    .card-bottom-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
      font-size: var(--text-xs2);

      .detail-item {
        display: flex;
        align-items: center;
        gap: 4px;

        .detail-label {
          color: #6b7280;
          white-space: nowrap;
        }

        .detail-value {
          color: #111827;
          font-weight: 500;
          white-space: nowrap;
        }
      }
    }
  }
}
.highlight-row{
  background-color: #FFF3CD ;
    border: 1px solid #FFC107 ;
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
    border-radius: 8px;

&:hover{
    background-color: #FFE69C;
    border-color: #FFB300;
}
}
</style>
